import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowUpRightFromSquare,
  faChevronLeft,
  faChevronRight,
  faCircleInfo,
  faFolder,
  faLink,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faArrowUpRightFromSquare as faArrowUpRightFromSquareSolid,
  faFolder as faFolderSolid,
  faLink as faLinkSolid,
} from "@fortawesome/pro-solid-svg-icons";
import type { LinkLibraryWidgetDto } from "../../../types/dtos/content-dashboard/ContentDashboardWidgetDto";
import type {
  LinkLibraryFolderItem,
  LinkLibraryItem,
  LinkLibraryLink,
} from "../../../types/content-dashboard/LinkLibrary";
import { visibleLibraryItems } from "../../../helpers/contentDashboardWidgets";
import { formatLinkHost } from "../../../helpers/formatLinkHost";
import Popover from "../../common/Popover";
import SafeRenderHtml from "../../common/SafeRenderHtml";

interface LinkLibraryWidgetCardProps {
  widget: LinkLibraryWidgetDto;
}

const rowClassName =
  "group flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1";

function linkCountLabel(
  count: number,
  t: (key: string, defaultValue: string, options?: { count: number }) => string,
): string {
  if (count === 1) {
    return t("ContentDashboards.LinkLibrary.OneLink", "1 link");
  }
  return t("ContentDashboards.LinkLibrary.LinkCount", "{{count}} links", {
    count,
  });
}

function linkAddress(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === "/" ? "" : parsed.pathname.replace(/\/$/, "");
    return `${parsed.host}${path}${parsed.search}`;
  } catch {
    return url;
  }
}

type LibrarySearchHit =
  | { kind: "folder"; folder: LinkLibraryFolderItem; index: number; key: string }
  | { kind: "link"; link: LinkLibraryLink; folderName?: string; key: string };

function librarySearchHits(items: LinkLibraryItem[], query: string): LibrarySearchHit[] {
  const needle = query.toLowerCase();
  const includes = (value: string) => value.toLowerCase().includes(needle);
  const hits: LibrarySearchHit[] = [];

  items.forEach((item, index) => {
    if (item.kind === "folder") {
      if (includes(item.name)) {
        hits.push({ kind: "folder", folder: item, index, key: `search-folder-${index}` });
      }
      item.links.forEach((link, linkIndex) => {
        if (includes(link.name) || includes(linkAddress(link.url)) || includes(link.url)) {
          hits.push({
            kind: "link",
            link,
            folderName: item.name,
            key: `search-link-${index}-${linkIndex}`,
          });
        }
      });
      return;
    }

    if (includes(item.name) || includes(linkAddress(item.url)) || includes(item.url)) {
      hits.push({ kind: "link", link: item, key: `search-link-${index}` });
    }
  });

  return hits;
}

function HoverIcon({
  regular,
  solid,
  className,
}: {
  regular: IconDefinition;
  solid: IconDefinition;
  className?: string;
}) {
  return (
    <span className={`relative inline-grid place-items-center ${className ?? ""}`}>
      <FontAwesomeIcon
        icon={regular}
        className="col-start-1 row-start-1 transition-opacity duration-150 group-hover:opacity-0 motion-reduce:transition-none"
      />
      <FontAwesomeIcon
        icon={solid}
        className="col-start-1 row-start-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100 motion-reduce:transition-none"
      />
    </span>
  );
}

const LinkLibraryWidgetCard: React.FC<LinkLibraryWidgetCardProps> = ({ widget }) => {
  const { t } = useTranslation();
  const [openFolderIndex, setOpenFolderIndex] = useState<number | null>(null);
  const [isHelpPopoverOpen, setIsHelpPopoverOpen] = useState(false);
  const [listMotion, setListMotion] = useState<"enter" | "back" | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const visibleItems = visibleLibraryItems(widget.items ?? []);
  const hasFolders = visibleItems.some((item) => item.kind === "folder");
  const openItem = openFolderIndex === null ? undefined : visibleItems[openFolderIndex];
  const openFolder = openItem?.kind === "folder" ? openItem : undefined;
  const searchQuery = searchText.trim();
  const isFiltering = isSearching && searchQuery.length > 0;
  const searchHits = isFiltering ? librarySearchHits(visibleItems, searchQuery) : [];

  useEffect(() => {
    if (isSearching) {
      searchInputRef.current?.focus();
    }
  }, [isSearching]);

  if (visibleItems.length === 0) {
    return null;
  }

  const closeSearch = () => {
    setIsSearching(false);
    setSearchText("");
  };

  const openFolderAt = (index: number) => {
    closeSearch();
    setListMotion("enter");
    setOpenFolderIndex(index);
  };

  const closeFolder = () => {
    setListMotion("back");
    setOpenFolderIndex(null);
  };

  const listMotionClass =
    listMotion === "enter"
      ? "animate-slide-in-right motion-reduce:animate-none"
      : listMotion === "back"
        ? "animate-fade-in-up motion-reduce:animate-none"
        : "";

  return (
    <div className="group/card bg-white border border-gray-200 rounded-lg shadow-sm h-full flex flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        {isSearching ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="shrink-0 text-xs text-gray-400"
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  closeSearch();
                }
              }}
              placeholder={t("ContentDashboards.LinkLibrary.SearchLinks", "Search links")}
              aria-label={t("ContentDashboards.LinkLibrary.SearchLinks", "Search links")}
              className="w-full min-w-0 border-0 bg-transparent p-0 text-sm text-gray-900 shadow-none placeholder:text-gray-400 focus:outline-none focus:ring-0"
            />
            <button
              type="button"
              onClick={closeSearch}
              aria-label={t("ContentDashboards.LinkLibrary.CloseSearch", "Close search")}
              className="shrink-0 rounded text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <FontAwesomeIcon icon={faXmark} className="text-xs" />
            </button>
          </div>
        ) : openFolder ? (
          <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
            <button
              type="button"
              onClick={closeFolder}
              aria-label={t(
                "ContentDashboards.LinkLibrary.BackTo",
                "Back to {{title}}",
                { title: widget.title },
              )}
              className="inline-flex min-w-0 items-center gap-2 rounded text-gray-600 transition-colors duration-150 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="shrink-0 text-xs" />
              <span className="truncate">{widget.title}</span>
            </button>
            <span className="shrink-0 text-gray-400" aria-hidden="true">
              /
            </span>
            <span className="min-w-0 truncate font-semibold text-gray-900">
              {openFolder.name}
            </span>
          </div>
        ) : (
          <p className="min-w-0 flex-grow truncate font-semibold text-gray-900">{widget.title}</p>
        )}
        {hasFolders && !isSearching && (
          <button
            type="button"
            onClick={() => setIsSearching(true)}
            aria-label={t("Common.Search", "Search")}
            className="flex-shrink-0 rounded text-gray-400 opacity-100 transition-opacity duration-150 hover:text-gray-600 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/card:opacity-100"
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
          </button>
        )}
        {widget.helpText && (
          <Popover
            triggerElement={
              <button
                type="button"
                className="flex-shrink-0 text-gray-400 transition-colors duration-150 hover:text-gray-600"
                aria-label={t("ContentDashboards.LinkLibrary.Help", "Help")}
              >
                <FontAwesomeIcon icon={faCircleInfo} size="sm" />
              </button>
            }
            openState={isHelpPopoverOpen}
            onOpenStateChange={setIsHelpPopoverOpen}
            placement="top"
            showCloseIcon={true}
            darkMode={true}
          >
            <SafeRenderHtml
              htmlText={widget.helpText}
              containerClassName="text-sm text-white leading-relaxed"
            />
          </Popover>
        )}
      </div>

      {/*
        max-h-64 caps the list when this card sets its own height. When a taller
        sibling stretches the card, the wrapper gains a definite height and
        min-h-full (which takes precedence over max-height) lets the list fill
        it instead of leaving blank space under the cap.
      */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <ul
          key={isFiltering ? "search" : openFolderIndex ?? "root"}
          className={`max-h-64 min-h-full space-y-1 overflow-y-auto px-2 py-2 ${isFiltering ? "" : listMotionClass}`}
        >
          {isFiltering ? (
            searchHits.length === 0 ? (
              <li className="px-2 py-3 text-sm text-gray-500">
                {t("ContentDashboards.LinkLibrary.NoMatches", "No matches")}
              </li>
            ) : (
              searchHits.map((hit) =>
                hit.kind === "folder" ? (
                  <li key={hit.key}>
                    <FolderRow folder={hit.folder} onOpen={() => openFolderAt(hit.index)} />
                  </li>
                ) : (
                  <li key={hit.key}>
                    <LinkRow
                      link={hit.link}
                      detail={
                        hit.folderName
                          ? t(
                              "ContentDashboards.LinkLibrary.InFolder",
                              "In {{folder}}",
                              { folder: hit.folderName },
                            )
                          : undefined
                      }
                    />
                  </li>
                )
              )
            )
          ) : openFolder ? (
            openFolder.links.map((link, index) => (
              <li key={`link-${link.name}-${index}`}>
                <LinkRow link={link} />
              </li>
            ))
          ) : (
            visibleItems.map((item, index) =>
              item.kind === "folder" ? (
                <li key={`folder-${item.name}-${index}`}>
                  <FolderRow folder={item} onOpen={() => openFolderAt(index)} />
                </li>
              ) : (
                <li key={`link-${item.name}-${index}`}>
                  <LinkRow link={item} />
                </li>
              )
            )
          )}
        </ul>
      </div>
    </div>
  );
};

function FolderRow({
  folder,
  onOpen,
}: {
  folder: LinkLibraryFolderItem;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const countLabel = linkCountLabel(folder.links.length, t);

  return (
    <button type="button" className={rowClassName} onClick={onOpen}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg background-tint-bg text-primary">
        <HoverIcon regular={faFolder} solid={faFolderSolid} className="text-sm" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{folder.name}</span>
        <span className="block truncate text-xs text-gray-500">{countLabel}</span>
      </span>
      <FontAwesomeIcon
        icon={faChevronRight}
        className="shrink-0 text-xs text-gray-400 transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none"
        aria-hidden="true"
      />
    </button>
  );
}

function LinkRow({ link, detail }: { link: LinkLibraryLink; detail?: string }) {
  const { t } = useTranslation();

  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={rowClassName}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
        <HoverIcon regular={faLink} solid={faLinkSolid} className="text-sm" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{link.name}</span>
        <span className="block truncate text-xs text-gray-500">
          {detail ?? formatLinkHost(link.url)}
        </span>
      </span>
      <HoverIcon
        regular={faArrowUpRightFromSquare}
        solid={faArrowUpRightFromSquareSolid}
        className="shrink-0 text-xs text-gray-400"
      />
      <span className="sr-only">
        {t("ContentDashboards.LinkLibrary.OpensInNewTab", "Opens in a new tab")}
      </span>
    </a>
  );
}

export default LinkLibraryWidgetCard;
